import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Gamepad2, LogOut, LayoutDashboard, Package, CalendarDays, Trophy, Settings as SettingsIcon, MessageCircle, Check, X, Trash2, Plus, Edit2, Copy, Ticket, BarChart3, Palette, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import api, { formatApiError, formatIDR } from "@/lib/api";
import BookingNotifier from "@/components/BookingNotifier";
import BrandLogo from "@/components/BrandLogo";

const TABS = [
  { key: "bookings", label: "Booking", icon: CalendarDays },
  { key: "units", label: "Unit PS", icon: Package },
  { key: "events", label: "Event", icon: Trophy },
  { key: "coupons", label: "Kupon", icon: Ticket },
  { key: "adstats", label: "Statistik Iklan", icon: BarChart3 },
  { key: "settings", label: "Pengaturan", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const { admin, loading, logout } = useAuth();
  const [tab, setTab] = useState("bookings");

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white">Loading...</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="md:w-64 md:min-h-screen bg-[#0F0F11] border-r border-white/10 flex flex-col">
        <div className="p-6 border-b border-white/10 flex items-center justify-between gap-2">
          <Link to="/" className="min-w-0">
            <BrandLogo size="sm" subtitle="Admin" />
          </Link>
          <BookingNotifier onOpenBookings={() => setTab("bookings")} />
        </div>

        <nav className="flex-1 py-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`w-full flex items-center gap-3 px-6 py-3.5 text-sm font-semibold uppercase tracking-wider transition-colors ${
                tab === t.key
                  ? "bg-[#00E5FF]/10 text-[#00E5FF] border-l-2 border-[#00E5FF]"
                  : "text-gray-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
              }`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-widest">Signed in</div>
          <div className="text-sm text-white mb-3 truncate">{admin.email}</div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center justify-center gap-2 border border-white/20 text-white py-2 text-xs uppercase tracking-widest hover:border-[#FF0055] hover:text-[#FF0055] transition-colors"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10 bg-[#0A0A0B]">
        {tab === "bookings" && <BookingsPanel />}
        {tab === "units" && <UnitsPanel />}
        {tab === "events" && <EventsPanel />}
        {tab === "coupons" && <CouponsPanel />}
        {tab === "adstats" && <AdStatsPanel />}
        {tab === "settings" && <SettingsPanel />}
      </main>
    </div>
  );
}

/* ---------------- Bookings ---------------- */
function BookingsPanel() {
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState("all");
  const [viewProof, setViewProof] = useState(null);

  const load = () => {
    api.get("/admin/bookings").then((r) => setBookings(r.data));
  };

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status });
      toast.success(status === "confirmed" ? "Booking dikonfirmasi" : "Booking ditolak");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sendWA = (b, isConfirmed) => {
    const businessName = settings?.business_name || "Rental PS";
    const adminWA = settings?.admin_whatsapp || "";
    const wa = b.whatsapp_number.replace(/\D/g, "").replace(/^0/, "62");
    const msg = isConfirmed
      ? `Halo *${b.customer_name}*! 🎮\n\nBooking kamu di *${businessName}* telah *DIKONFIRMASI LUNAS* ✅\n\n📅 Tanggal: ${b.booking_date}\n⏰ Jam: ${b.start_time} (${b.duration_hours} jam)\n🎯 Unit: ${b.unit_name} (${b.console_type})\n💰 Total: ${formatIDR(b.total_price)}\n\nSlot kamu sudah diamankan. Sampai jumpa!\n\n_Salam,_\n*${businessName}*`
      : `Halo *${b.customer_name}*,\n\nMohon maaf, booking kamu di *${businessName}* dengan detail berikut *tidak dapat kami proses* ❌\n\n📅 ${b.booking_date} ⏰ ${b.start_time}\n🎯 ${b.unit_name}\n\nSilakan hubungi admin${adminWA ? ` (${adminWA})` : ""} untuk info lebih lanjut.\n\n_${businessName}_`;
    const link = `https://wa.me/${wa}?text=${encodeURIComponent(msg)}`;
    window.open(link, "_blank");
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus booking ini?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Booking dihapus");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const statusBadge = (s) => ({
    pending: "bg-yellow-500/20 text-yellow-500 border-yellow-500/40",
    confirmed: "bg-[#00E5FF]/20 text-[#00E5FF] border-[#00E5FF]/40",
    rejected: "bg-[#FF0055]/20 text-[#FF0055] border-[#FF0055]/40",
  })[s];

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#00E5FF] mb-2">Manajemen</div>
          <h1 className="font-heading text-3xl font-black text-white tracking-tighter">BOOKING & PEMBAYARAN</h1>
        </div>
        <div className="flex gap-2">
          {["all", "pending", "confirmed", "rejected"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold border transition-colors ${
                filter === f ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "text-gray-400 border-white/10 hover:border-white/30"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total", val: bookings.length, color: "text-white" },
          { label: "Menunggu", val: bookings.filter((b) => b.status === "pending").length, color: "text-yellow-500" },
          { label: "Confirmed", val: bookings.filter((b) => b.status === "confirmed").length, color: "text-[#00E5FF]" },
          { label: "Rejected", val: bookings.filter((b) => b.status === "rejected").length, color: "text-[#FF0055]" },
        ].map((s) => (
          <div key={s.label} className="border border-white/10 bg-[#0F0F11] p-5">
            <div className="text-xs uppercase tracking-widest text-gray-500">{s.label}</div>
            <div className={`font-heading text-3xl font-black mt-2 ${s.color}`}>{s.val}</div>
          </div>
        ))}
      </div>

      <div className="border border-white/10 bg-[#0F0F11]">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-500">Tidak ada booking.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map((b) => (
              <div key={b.id} className="p-5 hover:bg-white/[0.02] transition-colors" data-testid={`booking-row-${b.id}`}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                  <div className="lg:col-span-3">
                    <div className={`inline-block px-2 py-0.5 border text-[10px] font-black uppercase tracking-widest ${statusBadge(b.status)}`}>
                      {b.status}
                    </div>
                    <div className="font-heading font-bold text-white mt-2">{b.customer_name}</div>
                    <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                      <MessageCircle size={12} /> +{b.whatsapp_number}
                    </div>
                  </div>
                  <div className="lg:col-span-3">
                    <div className="text-xs text-gray-500 uppercase tracking-widest">Unit</div>
                    <div className="text-white font-semibold mt-1">{b.unit_name}</div>
                    <div className="text-xs text-gray-400">{b.console_type}</div>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="text-xs text-gray-500 uppercase tracking-widest">Jadwal</div>
                    <div className="text-white text-sm mt-1">{b.booking_date}</div>
                    <div className="text-xs text-gray-400">{b.start_time} • {b.duration_hours}j</div>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="text-xs text-gray-500 uppercase tracking-widest">Total</div>
                    <div className="font-heading font-black text-[#00E5FF] mt-1">{formatIDR(b.total_price)}</div>
                    {b.payment_proof_base64 && (
                      <button onClick={() => setViewProof(b.payment_proof_base64)} data-testid={`view-proof-${b.id}`} className="text-xs text-[#00E5FF] hover:underline mt-1">
                        Lihat bukti
                      </button>
                    )}
                  </div>
                  <div className="lg:col-span-2 flex flex-wrap gap-2 justify-end">
                    {b.status === "pending" && (
                      <>
                        <button
                          onClick={async () => {
                            await updateStatus(b.id, "confirmed");
                            sendWA(b, true);
                          }}
                          data-testid={`confirm-${b.id}`}
                          className="inline-flex items-center gap-1 bg-[#00E5FF] text-black px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors"
                          title="Konfirmasi Lunas + Kirim WA"
                        >
                          <Check size={12} /> Lunas
                        </button>
                        <button
                          onClick={() => updateStatus(b.id, "rejected")}
                          data-testid={`reject-${b.id}`}
                          className="inline-flex items-center gap-1 border border-[#FF0055] text-[#FF0055] px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#FF0055] hover:text-white transition-colors"
                        >
                          <X size={12} /> Tolak
                        </button>
                      </>
                    )}
                    {b.status === "confirmed" && (
                      <button
                        onClick={() => sendWA(b, true)}
                        data-testid={`resend-wa-${b.id}`}
                        className="inline-flex items-center gap-1 border border-green-500 text-green-500 px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-green-500 hover:text-black transition-colors"
                      >
                        <MessageCircle size={12} /> Kirim WA
                      </button>
                    )}
                    <button
                      onClick={() => remove(b.id)}
                      data-testid={`delete-${b.id}`}
                      className="inline-flex items-center border border-white/20 text-gray-400 p-2 hover:border-[#FF0055] hover:text-[#FF0055] transition-colors"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                {b.notes && <div className="mt-3 pt-3 border-t border-white/5 text-xs text-gray-400"><span className="text-gray-500">Catatan:</span> {b.notes}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {viewProof && (
        <div onClick={() => setViewProof(null)} className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" data-testid="proof-modal">
          <img src={viewProof} alt="Bukti" className="max-h-[90vh] max-w-full" />
        </div>
      )}
    </div>
  );
}

/* ---------------- Units ---------------- */
function UnitsPanel() {
  const [units, setUnits] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/admin/units").then((r) => setUnits(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/admin/units/${editing.id}`, data);
        toast.success("Unit diupdate");
      } else {
        await api.post("/admin/units", data);
        toast.success("Unit dibuat");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus unit?")) return;
    await api.delete(`/admin/units/${id}`);
    toast.success("Unit dihapus");
    load();
  };

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#00E5FF] mb-2">Kelola</div>
          <h1 className="font-heading text-3xl font-black text-white tracking-tighter">UNIT PS</h1>
        </div>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          data-testid="add-unit-btn"
          className="inline-flex items-center gap-2 bg-[#00E5FF] text-black font-bold px-5 py-3 uppercase tracking-widest text-xs hover:bg-white transition-colors"
        >
          <Plus size={14} /> Tambah Unit
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {units.map((u) => (
          <div key={u.id} className="border border-white/10 bg-[#0F0F11] p-5" data-testid={`admin-unit-${u.id}`}>
            <div className="flex items-start justify-between">
              <div className="px-2 py-1 bg-white/10 text-[10px] font-black uppercase tracking-widest">{u.console_type}</div>
              <div className={`w-2 h-2 rounded-full ${u.is_active ? "bg-green-500" : "bg-gray-600"}`} />
            </div>
            <div className="font-heading font-bold text-white mt-3">{u.name}</div>
            <div className="text-xs text-gray-400 mt-1 line-clamp-2">{u.description}</div>
            <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
              <div className="font-heading font-black text-[#00E5FF]">{formatIDR(u.price_per_hour)}/jam</div>
              <div className="flex gap-1">
                <button onClick={() => { setEditing(u); setShowForm(true); }} data-testid={`edit-unit-${u.id}`} className="p-2 border border-white/10 hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors">
                  <Edit2 size={12} />
                </button>
                <button onClick={() => remove(u.id)} className="p-2 border border-white/10 hover:border-[#FF0055] hover:text-[#FF0055] transition-colors">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && <UnitForm initial={editing} onSave={save} onClose={() => { setShowForm(false); setEditing(null); }} />}
    </div>
  );
}

function UnitForm({ initial, onSave, onClose }) {
  const [f, setF] = useState({
    name: initial?.name || "",
    console_type: initial?.console_type || "PS5",
    price_per_hour: initial?.price_per_hour || 15000,
    description: initial?.description || "",
    image_url: initial?.image_url || "",
    is_active: initial?.is_active ?? true,
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0F0F11] border border-white/10 p-6 max-w-lg w-full" data-testid="unit-form">
        <h3 className="font-heading text-xl font-bold text-white mb-5">{initial ? "Edit Unit" : "Tambah Unit"}</h3>
        <div className="space-y-4">
          <input placeholder="Nama unit" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="unit-name-input" className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none" />
          <div className="grid grid-cols-2 gap-3">
            <select value={f.console_type} onChange={(e) => setF({ ...f, console_type: e.target.value })} className="bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none">
              <option>PS3</option><option>PS4</option><option>PS5</option>
            </select>
            <input type="number" placeholder="Harga/jam" value={f.price_per_hour} onChange={(e) => setF({ ...f, price_per_hour: Number(e.target.value) })} data-testid="unit-price-input" className="bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none" />
          </div>
          <input placeholder="URL gambar (opsional)" value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none" />
          <textarea placeholder="Deskripsi" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none resize-none" />
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} /> Aktif
          </label>
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={onClose} className="flex-1 border border-white/20 text-white py-3 text-xs uppercase tracking-widest hover:border-white/40 transition-colors">Batal</button>
          <button onClick={() => onSave(f)} data-testid="save-unit-btn" className="flex-1 bg-[#00E5FF] text-black font-bold py-3 text-xs uppercase tracking-widest hover:bg-white transition-colors">Simpan</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Events ---------------- */
function EventsPanel() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/admin/events/${editing.id}`, data);
        toast.success("Event diupdate");
      } else {
        await api.post("/admin/events", data);
        toast.success("Event dibuat");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus event?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Event dihapus");
    load();
  };

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#FF0055] mb-2">Kelola</div>
          <h1 className="font-heading text-3xl font-black text-white tracking-tighter">EVENT & TURNAMEN</h1>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-event-btn" className="inline-flex items-center gap-2 bg-[#00E5FF] text-black font-bold px-5 py-3 uppercase tracking-widest text-xs hover:bg-white transition-colors">
          <Plus size={14} /> Tambah Event
        </button>
      </div>

      <div className="space-y-4">
        {events.length === 0 && <div className="border border-white/10 bg-[#0F0F11] p-10 text-center text-gray-500">Belum ada event.</div>}
        {events.map((e) => (
          <div key={e.id} className="border border-white/10 bg-[#0F0F11] p-5" data-testid={`admin-event-${e.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className={`inline-block px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${
                  e.status === "ongoing" ? "bg-[#FF0055] text-white" : e.status === "upcoming" ? "bg-[#00E5FF] text-black" : "bg-gray-700 text-white"
                }`}>{e.status}</div>
                <div className="font-heading font-bold text-white mt-2">{e.title}</div>
                <div className="text-xs text-gray-400 mt-1">{e.game} • {e.event_date}</div>
                <div className="text-xs text-gray-400 mt-1">Prize: <span className="text-[#00E5FF]">{e.prize_pool || "-"}</span> • Peserta: {e.participants?.length || 0} • Matches: {e.matches?.length || 0}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setEditing(e); setShowForm(true); }} data-testid={`edit-event-${e.id}`} className="p-2 border border-white/10 hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors">
                  <Edit2 size={12} />
                </button>
                <button onClick={() => remove(e.id)} className="p-2 border border-white/10 hover:border-[#FF0055] hover:text-[#FF0055] transition-colors">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && <EventForm initial={editing} onSave={save} onClose={() => { setShowForm(false); setEditing(null); }} />}
    </div>
  );
}

function EventForm({ initial, onSave, onClose }) {
  const [f, setF] = useState({
    title: initial?.title || "",
    event_type: initial?.event_type || "tournament",
    status: initial?.status || "upcoming",
    game: initial?.game || "",
    description: initial?.description || "",
    event_date: initial?.event_date || "",
    prize_pool: initial?.prize_pool || "",
    entry_fee: initial?.entry_fee || "",
    registration_link: initial?.registration_link || "",
    participants: initial?.participants || [],
    matches: initial?.matches || [],
    poster_base64: initial?.poster_base64 || "",
  });
  const [newP, setNewP] = useState("");

  const addMatch = () => setF({ ...f, matches: [...f.matches, { round: "", player1: "", player2: "", score1: 0, score2: 0, status: "upcoming" }] });
  const updMatch = (i, k, v) => {
    const m = [...f.matches];
    m[i] = { ...m[i], [k]: k.startsWith("score") ? Number(v) : v };
    setF({ ...f, matches: m });
  };
  const rmMatch = (i) => setF({ ...f, matches: f.matches.filter((_, x) => x !== i) });

  const posterUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const r = new FileReader();
    r.onloadend = () => setF({ ...f, poster_base64: r.result });
    r.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0F0F11] border border-white/10 p-6 max-w-2xl w-full my-8" data-testid="event-form">
        <h3 className="font-heading text-xl font-bold text-white mb-5">{initial ? "Edit Event" : "Tambah Event"}</h3>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <input placeholder="Judul event" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="event-title-input" className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none" />
          <div className="grid grid-cols-3 gap-3">
            <select value={f.event_type} onChange={(e) => setF({ ...f, event_type: e.target.value })} className="bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none">
              <option value="tournament">Turnamen</option><option value="event">Event</option>
            </select>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="event-status-select" className="bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none">
              <option value="upcoming">Upcoming</option><option value="ongoing">Ongoing</option><option value="finished">Finished</option>
            </select>
            <input type="date" value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })} className="bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none [color-scheme:dark]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Game (ex: Mobile Legends)" value={f.game} onChange={(e) => setF({ ...f, game: e.target.value })} className="bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none" />
            <input placeholder="Prize pool" value={f.prize_pool} onChange={(e) => setF({ ...f, prize_pool: e.target.value })} className="bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Entry fee" value={f.entry_fee} onChange={(e) => setF({ ...f, entry_fee: e.target.value })} className="bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none" />
            <input placeholder="Link pendaftaran" value={f.registration_link} onChange={(e) => setF({ ...f, registration_link: e.target.value })} className="bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none" />
          </div>
          <textarea placeholder="Deskripsi" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none resize-none" />

          <div>
            <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Poster</label>
            <input type="file" accept="image/*" onChange={posterUpload} className="text-sm text-gray-400" />
            {f.poster_base64 && <img src={f.poster_base64} className="mt-2 max-h-32" alt="poster" />}
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Peserta</label>
            <div className="flex gap-2 mb-2">
              <input value={newP} onChange={(e) => setNewP(e.target.value)} placeholder="Nama peserta/tim" className="flex-1 bg-[#0A0A0B] border border-white/20 text-white px-3 py-2 focus:border-[#00E5FF] outline-none text-sm" />
              <button onClick={() => { if (newP.trim()) { setF({ ...f, participants: [...f.participants, newP.trim()] }); setNewP(""); } }} className="bg-[#00E5FF] text-black px-3 text-xs uppercase font-bold">Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {f.participants.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-white/5 border border-white/10 px-2 py-1 text-xs text-white">
                  {p} <button onClick={() => setF({ ...f, participants: f.participants.filter((_, x) => x !== i) })} className="text-[#FF0055]"><X size={10} /></button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Bagan Match</label>
              <button onClick={addMatch} className="text-xs text-[#00E5FF] uppercase font-bold">+ Match</button>
            </div>
            <div className="space-y-2">
              {f.matches.map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input placeholder="Round" value={m.round} onChange={(e) => updMatch(i, "round", e.target.value)} className="col-span-2 bg-[#0A0A0B] border border-white/10 text-white px-2 py-2 text-xs outline-none" />
                  <input placeholder="Player 1" value={m.player1} onChange={(e) => updMatch(i, "player1", e.target.value)} className="col-span-3 bg-[#0A0A0B] border border-white/10 text-white px-2 py-2 text-xs outline-none" />
                  <input type="number" value={m.score1} onChange={(e) => updMatch(i, "score1", e.target.value)} className="col-span-1 bg-[#0A0A0B] border border-white/10 text-white px-2 py-2 text-xs outline-none text-center" />
                  <input type="number" value={m.score2} onChange={(e) => updMatch(i, "score2", e.target.value)} className="col-span-1 bg-[#0A0A0B] border border-white/10 text-white px-2 py-2 text-xs outline-none text-center" />
                  <input placeholder="Player 2" value={m.player2} onChange={(e) => updMatch(i, "player2", e.target.value)} className="col-span-3 bg-[#0A0A0B] border border-white/10 text-white px-2 py-2 text-xs outline-none" />
                  <select value={m.status} onChange={(e) => updMatch(i, "status", e.target.value)} className="col-span-1 bg-[#0A0A0B] border border-white/10 text-white text-[10px] outline-none">
                    <option>upcoming</option><option>ongoing</option><option>finished</option>
                  </select>
                  <button onClick={() => rmMatch(i)} className="col-span-1 text-[#FF0055]"><X size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 flex gap-3 pt-4 border-t border-white/5">
          <button onClick={onClose} className="flex-1 border border-white/20 text-white py-3 text-xs uppercase tracking-widest hover:border-white/40 transition-colors">Batal</button>
          <button onClick={() => onSave(f)} data-testid="save-event-btn" className="flex-1 bg-[#00E5FF] text-black font-bold py-3 text-xs uppercase tracking-widest hover:bg-white transition-colors">Simpan</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Settings ---------------- */
function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const { refresh: refreshBrand } = useBrand();

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  if (!s) return <div className="text-gray-400">Loading...</div>;

  const update = (patch) => setS({ ...s, ...patch });

  const qrisUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Max 2MB");
    const r = new FileReader();
    r.onloadend = () => update({ qris_image_base64: r.result });
    r.readAsDataURL(file);
  };

  const logoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) return toast.error("Max 1MB");
    const r = new FileReader();
    r.onloadend = () => update({ brand_logo_base64: r.result });
    r.readAsDataURL(file);
  };

  const addWallet = () => update({ ewallets: [...(s.ewallets || []), { name: "", number: "", holder: "" }] });
  const updWallet = (i, k, v) => {
    const w = [...s.ewallets];
    w[i] = { ...w[i], [k]: v };
    update({ ewallets: w });
  };
  const rmWallet = (i) => update({ ewallets: s.ewallets.filter((_, x) => x !== i) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Pengaturan disimpan");
      refreshBrand();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#00E5FF] mb-2">Kelola</div>
        <h1 className="font-heading text-3xl font-black text-white tracking-tighter">PENGATURAN PEMBAYARAN</h1>
      </div>

      {/* Branding & Theme */}
      <div className="border border-white/10 bg-[#0F0F11] p-6 mb-6" data-testid="branding-section">
        <div className="flex items-center gap-2 mb-1">
          <Palette size={18} className="text-[#00E5FF]" />
          <h3 className="font-heading text-lg font-bold text-white">Branding & Tema</h3>
        </div>
        <p className="text-xs text-gray-500 mb-5">Perubahan langsung diterapkan di seluruh tampilan (public + admin) setelah disimpan.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Nama Brand / Website</label>
              <input
                value={s.brand_name || ""}
                onChange={(e) => update({ brand_name: e.target.value })}
                placeholder="RENTAL.PS"
                data-testid="brand-name-input"
                className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none font-heading font-bold text-lg tracking-tight"
              />
              <div className="text-xs text-gray-500 mt-1">Tips: pakai titik untuk efek dua-warna otomatis, mis. <span className="text-white font-mono">MIYU.PS</span> → MIYU<span className="text-[#00E5FF]">.PS</span></div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Logo Custom (opsional)</label>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center bg-[#00E5FF] text-black overflow-hidden">
                  {s.brand_logo_base64 ? (
                    <img src={s.brand_logo_base64} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <Gamepad2 size={28} strokeWidth={2.5} />
                  )}
                </div>
                <label className="cursor-pointer inline-flex items-center gap-2 border border-white/20 text-white px-4 py-2.5 text-xs uppercase tracking-widest font-semibold hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors">
                  <Upload size={14} /> {s.brand_logo_base64 ? "Ganti" : "Upload"}
                  <input type="file" accept="image/*" onChange={logoUpload} className="hidden" data-testid="brand-logo-upload" />
                </label>
                {s.brand_logo_base64 && (
                  <button onClick={() => update({ brand_logo_base64: "" })} className="text-xs text-[#FF0055] hover:underline uppercase tracking-widest" data-testid="brand-logo-remove">Reset</button>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-2">Rekomendasi: PNG/JPG persegi, max 1MB. Kosongkan untuk pakai ikon default.</div>
            </div>
          </div>

          <div className="space-y-4">
            <ColorField
              label="Warna Primary (Aksen Utama)"
              testId="theme-primary"
              value={s.theme_primary || "#00E5FF"}
              onChange={(v) => update({ theme_primary: v })}
              hint="Warna tombol utama, highlight menu aktif, hero accent"
            />
            <ColorField
              label="Warna Accent (Live / Turnamen)"
              testId="theme-accent"
              value={s.theme_accent || "#FF0055"}
              onChange={(v) => update({ theme_accent: v })}
              hint="Warna label LIVE, tolak booking, badge notifikasi"
            />
            <div className="p-3 border border-white/10 bg-[#0A0A0B]">
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">Preview</div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: s.theme_primary, color: "#000" }}>Primary CTA</span>
                <span className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest border" style={{ borderColor: s.theme_primary, color: s.theme_primary }}>Outlined</span>
                <span className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: s.theme_accent, color: "#fff" }}>● LIVE</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-white/10 bg-[#0F0F11] p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4">Info Usaha</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Nama Usaha</label>
              <input value={s.business_name} onChange={(e) => update({ business_name: e.target.value })} data-testid="business-name-input" className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Nomor WA Admin (Pengirim Notifikasi)</label>
              <input value={s.admin_whatsapp} onChange={(e) => update({ admin_whatsapp: e.target.value })} data-testid="admin-wa-input" placeholder="6281234567890" className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none" />
              <div className="text-xs text-gray-500 mt-1">Format internasional (62...), akan tampil di pesan konfirmasi.</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Instruksi Booking</label>
              <textarea value={s.booking_instructions} onChange={(e) => update({ booking_instructions: e.target.value })} rows={3} className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none resize-none" />
            </div>
            <div className="pt-3 border-t border-white/5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-widest text-gray-300 font-semibold">Floating Chat WhatsApp</label>
                <button
                  type="button"
                  onClick={() => update({ whatsapp_float_enabled: !(s.whatsapp_float_enabled !== false) })}
                  data-testid="wa-float-toggle"
                  className="flex items-center gap-2"
                >
                  <span className={`text-[10px] font-black uppercase tracking-widest ${s.whatsapp_float_enabled !== false ? "text-[#00E5FF]" : "text-gray-500"}`}>
                    {s.whatsapp_float_enabled !== false ? "ON" : "OFF"}
                  </span>
                  <span className={`relative w-10 h-5 border transition-colors ${s.whatsapp_float_enabled !== false ? "bg-[#00E5FF] border-[#00E5FF]" : "bg-transparent border-white/30"}`}>
                    <span className={`absolute top-0.5 w-3.5 h-3.5 transition-transform ${s.whatsapp_float_enabled !== false ? "translate-x-5 bg-black" : "translate-x-0.5 bg-white/60"}`} />
                  </span>
                </button>
              </div>
              <input
                value={s.whatsapp_float_message || ""}
                onChange={(e) => update({ whatsapp_float_message: e.target.value })}
                placeholder="Halo, saya mau tanya tentang rental PS"
                data-testid="wa-float-message-input"
                className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-2.5 focus:border-[#00E5FF] outline-none text-sm"
              />
              <div className="text-xs text-gray-500 mt-1">Pesan default yang akan pre-fill saat customer klik chat.</div>
            </div>
            <div className="pt-3 border-t border-white/5">
              <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Google Analytics 4 (GA4) Measurement ID</label>
              <input
                value={s.ga4_measurement_id || ""}
                onChange={(e) => update({ ga4_measurement_id: e.target.value })}
                placeholder="G-XXXXXXXXXX"
                data-testid="ga4-id-input"
                className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none font-mono text-sm"
              />
              <div className="text-xs text-gray-500 mt-1">Otomatis load gtag.js + track page views & booking conversion. Ambil ID di analytics.google.com → Property Settings.</div>
            </div>
          </div>
        </div>

        <div className="border border-white/10 bg-[#0F0F11] p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4">QRIS</h3>
          <input type="file" accept="image/*" onChange={qrisUpload} data-testid="qris-upload" className="text-sm text-gray-400" />
          {s.qris_image_base64 && (
            <div className="mt-3">
              <img src={s.qris_image_base64} alt="QRIS" className="max-h-48 bg-white p-2" />
              <button onClick={() => update({ qris_image_base64: "" })} className="mt-2 text-xs text-[#FF0055]">Hapus QRIS</button>
            </div>
          )}
        </div>

        <div className="border border-white/10 bg-[#0F0F11] p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold text-white">E-Wallet / Rekening</h3>
            <button onClick={addWallet} data-testid="add-wallet-btn" className="text-xs text-[#00E5FF] uppercase font-bold">+ Tambah</button>
          </div>
          <div className="space-y-3">
            {(s.ewallets || []).map((w, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input placeholder="Nama (DANA)" value={w.name} onChange={(e) => updWallet(i, "name", e.target.value)} className="col-span-3 bg-[#0A0A0B] border border-white/10 text-white px-3 py-2 text-sm outline-none focus:border-[#00E5FF]" />
                <input placeholder="Nomor" value={w.number} onChange={(e) => updWallet(i, "number", e.target.value)} className="col-span-4 bg-[#0A0A0B] border border-white/10 text-white px-3 py-2 text-sm outline-none focus:border-[#00E5FF]" />
                <input placeholder="Atas nama" value={w.holder} onChange={(e) => updWallet(i, "holder", e.target.value)} className="col-span-4 bg-[#0A0A0B] border border-white/10 text-white px-3 py-2 text-sm outline-none focus:border-[#00E5FF]" />
                <button onClick={() => rmWallet(i)} className="col-span-1 text-[#FF0055] flex justify-center"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-white/10 bg-[#0F0F11] p-6 lg:col-span-2" data-testid="adsense-settings-section">
          <h3 className="font-heading text-lg font-bold text-white mb-1">Kelola Iklan (Google AdSense)</h3>
          <p className="text-xs text-gray-500 mb-5">Isi Publisher ID untuk load loader script otomatis, atau paste script/snippet lengkap. Gunakan toggle ON/OFF di tiap slot untuk menyembunyikan iklan sementara.</p>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Publisher ID</label>
                <input
                  value={s.adsense_publisher_id || ""}
                  onChange={(e) => update({ adsense_publisher_id: e.target.value })}
                  placeholder="ca-pub-1234567890123456"
                  data-testid="adsense-publisher-id-input"
                  className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none font-mono text-sm"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Script Loader (override, opsional)</label>
                <textarea
                  value={s.adsense_script || ""}
                  onChange={(e) => update({ adsense_script: e.target.value })}
                  rows={3}
                  placeholder='<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-XXX" crossorigin="anonymous"></script>'
                  data-testid="adsense-script-input"
                  className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none font-mono text-xs resize-none"
                />
              </div>
            </div>

            <AdSlotEditor
              title="Slot Header (Beranda)"
              testId="header"
              value={s.ad_slot_header}
              onChange={(v) => update({ ad_slot_header: v })}
              enabled={s.ad_header_enabled !== false}
              onToggle={(v) => update({ ad_header_enabled: v })}
            />
            <AdSlotEditor
              title="Slot Katalog Unit (Beranda)"
              testId="catalog"
              value={s.ad_slot_catalog}
              onChange={(v) => update({ ad_slot_catalog: v })}
              enabled={s.ad_catalog_enabled !== false}
              onToggle={(v) => update({ ad_catalog_enabled: v })}
            />
            <AdSlotEditor
              title="Slot Atas Halaman Booking"
              testId="booking-top"
              value={s.ad_slot_booking_top}
              onChange={(v) => update({ ad_slot_booking_top: v })}
              enabled={s.ad_booking_top_enabled !== false}
              onToggle={(v) => update({ ad_booking_top_enabled: v })}
            />
            <AdSlotEditor
              title="Slot Bawah Halaman Booking"
              testId="booking-bottom"
              value={s.ad_slot_booking_bottom}
              onChange={(v) => update({ ad_slot_booking_bottom: v })}
              enabled={s.ad_booking_bottom_enabled !== false}
              onToggle={(v) => update({ ad_booking_bottom_enabled: v })}
            />
            <AdSlotEditor
              title="Slot Halaman Event & Turnamen"
              testId="events"
              value={s.ad_slot_events}
              onChange={(v) => update({ ad_slot_events: v })}
              enabled={s.ad_events_enabled !== false}
              onToggle={(v) => update({ ad_events_enabled: v })}
            />

            <div className="text-xs text-gray-500 leading-relaxed border-l-2 border-[#00E5FF]/40 pl-3">
              <strong className="text-gray-400">Tips:</strong> Salin kode "Unit Iklan" (snippet <code className="text-[#00E5FF]">&lt;ins&gt;</code>) dari dashboard AdSense ke tiap slot. Toggle OFF akan menyembunyikan iklan tanpa menghapus kode.
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <button onClick={save} disabled={saving} data-testid="save-settings-btn" className="bg-[#00E5FF] text-black font-bold px-8 py-3 uppercase tracking-widest text-xs hover:bg-white transition-colors disabled:opacity-50">
          {saving ? "Menyimpan..." : "Simpan Pengaturan"}
        </button>
      </div>
    </div>
  );
}

function AdSlotEditor({ title, testId, value, onChange, enabled, onToggle }) {
  return (
    <div
      className={`border p-4 transition-colors ${enabled ? "border-[#00E5FF]/30 bg-[#00E5FF]/[0.02]" : "border-white/10 bg-black/20"}`}
      data-testid={`ad-slot-editor-${testId}`}
    >
      <div className="flex items-center justify-between mb-3">
        <label className="text-xs uppercase tracking-widest text-gray-300 font-semibold">{title}</label>
        <button
          type="button"
          onClick={() => onToggle(!enabled)}
          data-testid={`ad-toggle-${testId}`}
          className="flex items-center gap-2 group"
        >
          <span className={`text-[10px] font-black uppercase tracking-widest ${enabled ? "text-[#00E5FF]" : "text-gray-500"}`}>
            {enabled ? "ON" : "OFF"}
          </span>
          <span
            className={`relative w-10 h-5 border transition-colors ${enabled ? "bg-[#00E5FF] border-[#00E5FF]" : "bg-transparent border-white/30"}`}
          >
            <span
              className={`absolute top-0.5 w-3.5 h-3.5 transition-transform ${enabled ? "translate-x-5 bg-black" : "translate-x-0.5 bg-white/60"}`}
            />
          </span>
        </button>
      </div>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder='<ins class="adsbygoogle" style="display:block" data-ad-client="ca-pub-XXX" data-ad-slot="1234567890" data-ad-format="auto" data-full-width-responsive="true"></ins>'
        data-testid={`ad-slot-${testId}-input`}
        className="w-full bg-[#0A0A0B] border border-white/20 text-white px-3 py-2 focus:border-[#00E5FF] outline-none font-mono text-xs resize-none"
      />
    </div>
  );
}


/* ---------------- Coupons ---------------- */
function CouponsPanel() {
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/admin/coupons/${editing.id}`, data);
        toast.success("Kupon diupdate");
      } else {
        await api.post("/admin/coupons", data);
        toast.success("Kupon dibuat");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus kupon?")) return;
    await api.delete(`/admin/coupons/${id}`);
    toast.success("Kupon dihapus");
    load();
  };

  return (
    <div data-testid="coupons-panel">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#00E5FF] mb-2">Promosi</div>
          <h1 className="font-heading text-3xl font-black text-white tracking-tighter">KUPON DISKON</h1>
          <p className="text-xs text-gray-500 mt-2">Kode voucher yang bisa dipakai customer di step data diri untuk potongan harga.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-coupon-btn" className="inline-flex items-center gap-2 bg-[#00E5FF] text-black font-bold px-5 py-3 uppercase tracking-widest text-xs hover:bg-white transition-colors">
          <Plus size={14} /> Buat Kupon
        </button>
      </div>

      <div className="border border-white/10 bg-[#0F0F11]">
        {coupons.length === 0 ? (
          <div className="p-10 text-center text-gray-500">Belum ada kupon.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {coupons.map((c) => (
              <div key={c.id} className="p-5 hover:bg-white/[0.02] transition-colors" data-testid={`coupon-row-${c.code}`}>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
                  <div className="md:col-span-2">
                    <div className={`inline-flex items-center gap-2 px-2 py-1 text-[10px] font-black uppercase tracking-widest ${c.active ? "bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/40" : "bg-white/5 text-gray-500 border border-white/10"}`}>
                      {c.active ? "Aktif" : "Nonaktif"}
                    </div>
                    <div className="font-heading font-black text-white text-lg mt-2 font-mono tracking-wider">{c.code}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-widest">Diskon</div>
                    <div className="text-white font-semibold mt-1">
                      {c.discount_type === "percent" ? `${c.discount_value}%` : formatIDR(c.discount_value)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-widest">Kuota</div>
                    <div className="text-white mt-1">
                      {c.used_count} / {c.max_uses > 0 ? c.max_uses : "∞"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-widest">Berlaku Sampai</div>
                    <div className="text-white text-sm mt-1">{c.expires_at || "Tanpa batas"}</div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => { setEditing(c); setShowForm(true); }} data-testid={`edit-coupon-${c.code}`} className="p-2 border border-white/10 hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors">
                      <Edit2 size={12} />
                    </button>
                    <button onClick={() => remove(c.id)} data-testid={`delete-coupon-${c.code}`} className="p-2 border border-white/10 hover:border-[#FF0055] hover:text-[#FF0055] transition-colors">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && <CouponForm initial={editing} onSave={save} onClose={() => { setShowForm(false); setEditing(null); }} />}
    </div>
  );
}

function CouponForm({ initial, onSave, onClose }) {
  const [f, setF] = useState({
    code: initial?.code || "",
    discount_type: initial?.discount_type || "percent",
    discount_value: initial?.discount_value || 10,
    max_uses: initial?.max_uses || 0,
    expires_at: initial?.expires_at || "",
    active: initial?.active ?? true,
  });

  const submit = () => {
    if (!f.code.trim()) return toast.error("Kode wajib diisi");
    onSave({ ...f, code: f.code.trim().toUpperCase(), expires_at: f.expires_at || null });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0F0F11] border border-white/10 p-6 max-w-lg w-full" data-testid="coupon-form">
        <h3 className="font-heading text-xl font-bold text-white mb-5">{initial ? "Edit Kupon" : "Buat Kupon Baru"}</h3>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Kode Kupon</label>
            <input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="coupon-code-input" placeholder="HEMAT10" className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none font-mono tracking-wider uppercase" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Tipe</label>
              <select value={f.discount_type} onChange={(e) => setF({ ...f, discount_type: e.target.value })} data-testid="coupon-type-select" className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none">
                <option value="percent">Persen (%)</option>
                <option value="amount">Nominal (Rp)</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Nilai</label>
              <input type="number" value={f.discount_value} onChange={(e) => setF({ ...f, discount_value: Number(e.target.value) })} data-testid="coupon-value-input" className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Kuota (0 = unlimited)</label>
              <input type="number" value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: Number(e.target.value) })} data-testid="coupon-quota-input" className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">Berlaku Sampai</label>
              <input type="date" value={f.expires_at || ""} onChange={(e) => setF({ ...f, expires_at: e.target.value })} className="w-full bg-[#0A0A0B] border border-white/20 text-white px-4 py-3 focus:border-[#00E5FF] outline-none [color-scheme:dark]" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Aktif
          </label>
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={onClose} className="flex-1 border border-white/20 text-white py-3 text-xs uppercase tracking-widest hover:border-white/40 transition-colors">Batal</button>
          <button onClick={submit} data-testid="save-coupon-btn" className="flex-1 bg-[#00E5FF] text-black font-bold py-3 text-xs uppercase tracking-widest hover:bg-white transition-colors">Simpan</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Ad Stats ---------------- */
function AdStatsPanel() {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(7);

  const load = (d) => api.get("/admin/ad-stats", { params: { days: d } }).then((r) => setStats(r.data));
  useEffect(() => { load(days); }, [days]);

  const slotLabels = {
    header: "Header (Beranda)",
    catalog: "Katalog Unit (Beranda)",
    booking_top: "Booking - Atas",
    booking_bottom: "Booking - Bawah",
    events: "Event & Turnamen",
  };

  return (
    <div data-testid="adstats-panel">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#00E5FF] mb-2">Analytics</div>
          <h1 className="font-heading text-3xl font-black text-white tracking-tighter">STATISTIK IKLAN</h1>
          <p className="text-xs text-gray-500 mt-2">Estimasi lokal: dihitung saat customer melihat & mengklik slot iklan di website ini. Untuk data revenue real, cek dashboard Google AdSense.</p>
        </div>
        <div className="flex gap-2">
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`stats-days-${d}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold border transition-colors ${
                days === d ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "text-gray-400 border-white/10 hover:border-white/30"
              }`}
            >
              {d === 1 ? "24 Jam" : `${d} Hari`}
            </button>
          ))}
        </div>
      </div>

      {!stats ? (
        <div className="text-gray-500">Memuat...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="border border-white/10 bg-[#0F0F11] p-6" data-testid="stat-total-impressions">
              <div className="text-xs uppercase tracking-widest text-gray-500">Total Impresi</div>
              <div className="font-heading text-4xl font-black text-white mt-2">{stats.total_impressions.toLocaleString("id-ID")}</div>
              <div className="text-xs text-gray-500 mt-1">Iklan dilihat customer</div>
            </div>
            <div className="border border-white/10 bg-[#0F0F11] p-6" data-testid="stat-total-clicks">
              <div className="text-xs uppercase tracking-widest text-gray-500">Total Klik</div>
              <div className="font-heading text-4xl font-black text-[#00E5FF] mt-2">{stats.total_clicks.toLocaleString("id-ID")}</div>
              <div className="text-xs text-gray-500 mt-1">Iklan diklik customer</div>
            </div>
            <div className="border border-white/10 bg-[#0F0F11] p-6" data-testid="stat-ctr">
              <div className="text-xs uppercase tracking-widest text-gray-500">CTR</div>
              <div className="font-heading text-4xl font-black text-[#FF0055] mt-2">{stats.overall_ctr}%</div>
              <div className="text-xs text-gray-500 mt-1">Click-Through Rate</div>
            </div>
          </div>

          <div className="border border-white/10 bg-[#0F0F11]">
            <div className="p-5 border-b border-white/5">
              <h3 className="font-heading font-bold text-white">Per Slot Iklan</h3>
            </div>
            {Object.keys(stats.by_slot).length === 0 ? (
              <div className="p-10 text-center text-gray-500 text-sm">Belum ada aktivitas iklan pada periode ini. Aktifkan slot iklan di menu Pengaturan.</div>
            ) : (
              <div className="divide-y divide-white/5">
                {Object.entries(stats.by_slot).map(([slot, data]) => (
                  <div key={slot} className="p-5 grid grid-cols-4 gap-4 items-center" data-testid={`stat-slot-${slot}`}>
                    <div className="font-heading font-bold text-white">{slotLabels[slot] || slot}</div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-widest">Impresi</div>
                      <div className="text-white font-semibold mt-1">{data.impressions.toLocaleString("id-ID")}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-widest">Klik</div>
                      <div className="text-[#00E5FF] font-semibold mt-1">{data.clicks.toLocaleString("id-ID")}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-widest">CTR</div>
                      <div className="text-[#FF0055] font-semibold mt-1">{data.ctr}%</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 p-4 border-l-2 border-yellow-500/60 bg-yellow-500/5 text-xs text-gray-400 leading-relaxed">
            <strong className="text-yellow-500 uppercase tracking-widest text-[10px]">Catatan:</strong> Statistik ini adalah <em>estimasi berbasis tracking lokal</em>, dihitung setiap kali slot iklan tampil atau diklik di website. Data revenue, RPM, dan detail pendapatan resmi hanya tersedia di dashboard Google AdSense.
          </div>
        </>
      )}
    </div>
  );
}


function ColorField({ label, testId, value, onChange, hint }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-gray-400 font-semibold block mb-2">{label}</label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-picker`}
          className="w-16 h-11 border border-white/20 bg-transparent cursor-pointer p-0"
          style={{ appearance: "none" }}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-hex`}
          maxLength={7}
          placeholder="#00E5FF"
          className="flex-1 bg-[#0A0A0B] border border-white/20 text-white px-4 py-2.5 focus:border-[#00E5FF] outline-none font-mono uppercase"
        />
      </div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

